import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import { subscriptionAPI } from '../services/api';
import { LoadingSpinner } from '../components/ui';
import type { SubscriptionTier } from '../types/platformOrganization';

const TIER_LABELS: Record<SubscriptionTier, string> = {
  BASIC: 'Básico',
  PROFESSIONAL: 'Profesional',
  PREMIUM: 'Premium',
};

const CompletePayment: React.FC = () => {
  const { user, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const sessionId = searchParams.get('session_id');

  const { data: plansRes, isLoading: plansLoading } = useQuery(['subscription-plans-public'], () =>
    subscriptionAPI.listPlans()
  );

  const pendingTier = (user?.subscription?.pendingTier || user?.subscription?.tier) as
    | SubscriptionTier
    | undefined;
  const checkoutUrl = user?.subscription?.checkoutUrl as string | null | undefined;
  const planLabel =
    pendingTier && TIER_LABELS[pendingTier]
      ? TIER_LABELS[pendingTier]
      : plansRes?.data?.data?.find((p: { tier: string }) => p.tier === pendingTier)?.label ||
        pendingTier ||
        'su plan';

  useEffect(() => {
    if (!sessionId) return;

    let cancelled = false;
    (async () => {
      try {
        await subscriptionAPI.getCheckoutStatus(sessionId);
        if (cancelled) return;
        await queryClient.invalidateQueries(['subscription-current']);
        toast.success('Pago confirmado. Recargando su cuenta…');
        const next = new URLSearchParams(searchParams);
        next.delete('session_id');
        setSearchParams(next, { replace: true });
        window.location.reload();
      } catch (e: unknown) {
        if (cancelled) return;
        const msg =
          (e as { response?: { data?: { error?: string; message?: string } } })?.response?.data
            ?.error ||
          (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'No se pudo confirmar el pago. Si ya pagó, espere unos segundos e inicie sesión de nuevo.';
        toast.error(msg);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId, queryClient, searchParams, setSearchParams]);

  const handlePay = () => {
    if (checkoutUrl) {
      window.location.href = checkoutUrl;
      return;
    }
    toast.error('No hay enlace de pago configurado. Contacte a soporte.');
  };

  return (
    <div className="min-h-screen bg-[#05080f] text-white flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.04] p-8 shadow-2xl text-center">
        <h1 className="text-2xl font-bold">Complete su membresía</h1>
        <p className="mt-3 text-sm text-slate-400 leading-relaxed">
          Su cuenta está creada, pero el acceso al panel queda bloqueado hasta confirmar el pago del
          plan <span className="text-white font-medium">{planLabel}</span> en Stripe.
        </p>

        {sessionId ? (
          <div className="mt-8 flex justify-center">
            <LoadingSpinner />
            <p className="ml-3 text-sm text-slate-400">Confirmando pago…</p>
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={handlePay}
              disabled={plansLoading || !checkoutUrl}
              className="mt-8 w-full rounded-lg bg-gradient-to-r from-indigo-500 to-violet-600 py-3 text-sm font-semibold text-white shadow-lg disabled:opacity-50"
            >
              {plansLoading ? 'Cargando…' : 'Ir a pagar con Stripe'}
            </button>
            <p className="mt-4 text-xs text-slate-500">
              Después del pago, vuelva aquí o inicie sesión de nuevo para acceder al dashboard.
            </p>
            <button
              type="button"
              onClick={() => logout()}
              className="mt-6 text-xs text-slate-400 hover:text-slate-300"
            >
              Cerrar sesión
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CompletePayment;
